import { IButtonStyles, useTheme } from '@fluentui/react';
import { IPActionButton } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapExtraSmall } from 'globalStyles';
import { ReactNode } from 'react';

export interface IFilterSwitchOption<TKey extends string | number = string> {
  key: TKey;
  label: ReactNode;
}

interface IFilterSwitchChipsProps<TKey extends string | number> {
  options: readonly IFilterSwitchOption<TKey>[];
  isSelected: (key: TKey) => boolean;
  onClick: (key: TKey) => void;
  /** A check mark in every selected chip: the cue that more than one can be on. */
  showCheck: boolean;
  stopPropagation?: boolean;
}

/**
 * The chip row behind both switches. Every chip is a real toggle button (aria-pressed), so Space
 * and Enter switch it like a click does.
 *
 * Colors are sourced from theme tokens that Fluent swaps per active theme, so no manual
 * dark-mode branching is needed:
 *  - Selected: theme.palette.themePrimary (accent) + theme.palette.white text.
 *  - Idle: theme.semanticColors.buttonBackground + buttonBorder + buttonText (UI affordance tokens).
 *    Hovering an idle chip colours its border only, so it reads as pressable without the text
 *    flickering to the accent colour the way Fluent's default hover does.
 */
function FilterSwitchChips<TKey extends string | number>({
  options,
  isSelected,
  onClick,
  showCheck,
  stopPropagation,
}: IFilterSwitchChipsProps<TKey>) {
  // useTheme() reads the <ThemeProvider> context so the tokens below are the
  // active theme's. getTheme() returns the default light theme in both modes.
  const theme = useTheme();

  const selectedBg = theme.palette.themePrimary;
  const selectedBorder = theme.palette.themePrimary;
  const selectedText = theme.palette.white;
  const idleBg = theme.semanticColors.buttonBackground;
  const idleBorder = theme.semanticColors.buttonBorder;
  const idleText = theme.semanticColors.buttonText;

  const getChipStyles = (selected: boolean): IButtonStyles => {
    const text = selected ? selectedText : idleText;
    // pinned in every state: Fluent's hover, pressed and checked styles each recolour the button
    const pinned = {
      backgroundColor: selected ? selectedBg : idleBg,
      borderColor: selected ? selectedBorder : idleBorder,
      color: text,
    };

    return {
      root: {
        ...pinned,
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 16,
        padding: '4px 10px',
        minWidth: 0,
        height: 28,
      },
      rootHovered: { ...pinned, borderColor: selectedBorder },
      rootPressed: pinned,
      rootChecked: pinned,
      rootCheckedHovered: pinned,
      rootCheckedPressed: pinned,
      label: { color: text, fontWeight: selected ? 600 : 400 },
      icon: { color: text, fontSize: 12 },
      iconHovered: { color: text },
      iconPressed: { color: text },
      iconChecked: { color: text },
    };
  };

  return (
    <IPStack horizontal wrap tokens={globalStackTokensGapExtraSmall}>
      {options.map((option) => {
        const selected = isSelected(option.key);

        return (
          <IPActionButton
            key={option.key}
            toggle
            checked={selected}
            iconProps={showCheck && selected ? { iconName: 'CheckMark' } : undefined}
            onClick={(ev) => {
              if (stopPropagation) ev.stopPropagation();
              onClick(option.key);
            }}
            styles={getChipStyles(selected)}
          >
            {option.label}
          </IPActionButton>
        );
      })}
    </IPStack>
  );
}

interface IFilterSwitchProps<TKey extends string | number = string> {
  options: readonly IFilterSwitchOption<TKey>[];
  /** Undefined when nothing is selected. */
  selectedKey?: TKey;
  onChange: (key: TKey) => void;
  /** Stops click propagation on each option (useful inside menus/callouts). */
  stopPropagation?: boolean;
}

/**
 * Pill-shaped segmented control used to switch between mutually-exclusive filter values
 * (e.g. "All / My / Shared / Template" view chooser). Clicking the selected chip reports its key
 * again; a caller that allows "nothing selected" clears on that (see CategoryChips).
 */
export function FilterSwitch<TKey extends string | number = string>({
  options,
  selectedKey,
  onChange,
  stopPropagation,
}: IFilterSwitchProps<TKey>) {
  return (
    <FilterSwitchChips
      options={options}
      isSelected={(key) => key === selectedKey}
      onClick={onChange}
      showCheck={false}
      stopPropagation={stopPropagation}
    />
  );
}

interface IFilterSwitchMultiProps<TKey extends string | number = string> {
  options: readonly IFilterSwitchOption<TKey>[];
  selectedKeys: readonly TKey[];
  /** The whole selection after the click, in the order the chips were switched on. */
  onChange: (keys: TKey[]) => void;
  stopPropagation?: boolean;
}

/**
 * The multi-select sibling: each chip toggles on its own and any number can be on, down to none.
 * Selected chips carry a check mark, the cue that this is not a switch between exclusive values.
 * Fully controlled: the selection lives with the caller and nothing here can drift from it.
 */
export function FilterSwitchMulti<TKey extends string | number = string>({
  options,
  selectedKeys,
  onChange,
  stopPropagation,
}: IFilterSwitchMultiProps<TKey>) {
  const toggle = (key: TKey) =>
    onChange(selectedKeys.includes(key) ? selectedKeys.filter((item) => item !== key) : [...selectedKeys, key]);

  return (
    <FilterSwitchChips
      options={options}
      isSelected={(key) => selectedKeys.includes(key)}
      onClick={toggle}
      showCheck
      stopPropagation={stopPropagation}
    />
  );
}
